import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useHistory } from "react-router-dom";

import { toast } from "react-toastify";
import openSocket from "../../services/socket-io";
import clsx from "clsx";

import {
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  makeStyles,
} from "@material-ui/core";

import ContactDrawer from "../ContactDrawer";
import MessageInput from "../MessageInput/";
import TicketHeader from "../TicketHeader";
import TicketInfo from "../TicketInfo";
import TicketActionButtons from "../TicketActionButtons";
import MessagesList from "../MessagesList";
import api from "../../services/api";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import toastError from "../../errors/toastError";

const drawerWidth = 320;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100%",
    position: "relative",
    overflow: "hidden",
  },

  ticketInfo: {
    maxWidth: "50%",
    flexBasis: "50%",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "80%",
      flexBasis: "80%",
    },
  },
  ticketActionButtons: {
    maxWidth: "50%",
    flexBasis: "50%",
    display: "flex",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "100%",
      flexBasis: "100%",
      marginBottom: "5px",
    },
  },
  internalToolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: theme.spacing(1, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.03)"
        : "rgba(63, 81, 181, 0.04)",
  },
  senderSelect: {
    minWidth: 190,
  },

  mainWrapper: {
    flex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderLeft: "0",
    marginRight: -drawerWidth,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },

  mainWrapperShift: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: 0,
  },
}));

const Ticket = () => {
  const { ticketId } = useParams();
  const history = useHistory();
  const classes = useStyles();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState({});
  const [ticket, setTicket] = useState({});
  const [internalSibling, setInternalSibling] = useState(null);
  const [sendTicketId, setSendTicketId] = useState(Number(ticketId));

  useEffect(() => {
    let active = true;
    const request = axios.CancelToken.source();
    setLoading(true);
    setContact({});
    setTicket({});
    setInternalSibling(null);
    setSendTicketId(Number(ticketId));
    const fetchTicket = async () => {
      try {
        const { data } = await api.get("/tickets/" + ticketId, {
          cancelToken: request.token,
        });
        if (!active) return;

        setContact(data.contact);
        setTicket(data);
        setInternalSibling(data.internalSibling || null);
        setSendTicketId(Number(data.id));
        setLoading(false);
      } catch (err) {
        if (!active || axios.isCancel(err)) return;
        setLoading(false);
        toastError(err);
      }
    };
    fetchTicket();
    return () => {
      active = false;
      request.cancel();
    };
  }, [ticketId, history]);

  useEffect(() => {
    const socket = openSocket();

    socket.on("connect", () => {
      socket.emit("joinChatBox", ticketId);
      if (internalSibling?.id) {
        socket.emit("joinChatBox", String(internalSibling.id));
      }
    });

    socket.on("ticket", (data) => {
      if (data.action === "update") {
        if (Number(data.ticket?.id) === Number(ticketId)) {
          setTicket(data.ticket);
        } else if (
          internalSibling?.id &&
          Number(data.ticket?.id) === Number(internalSibling.id)
        ) {
          setInternalSibling(data.ticket);
        }
      }

      if (data.action === "delete") {
        toast.success("Ticket deleted sucessfully.");
        history.push("/tickets");
      }
    });

    socket.on("contact", (data) => {
      if (data.action === "update") {
        setContact((prevState) => {
          if (prevState.id === data.contact?.id) {
            return { ...prevState, ...data.contact };
          }
          return prevState;
        });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [ticketId, history, internalSibling?.id]);

  useEffect(() => {
    if (ticket.attentionStatus !== "unseen" || !ticket.attentionMessageId) {
      return undefined;
    }
    const attentionMessageId = ticket.attentionMessageId;
    const timer = setTimeout(() => {
      api
        .post(`/tickets/${ticketId}/attention/seen`, { attentionMessageId })
        .catch(() => {});
    }, 2000);
    return () => clearTimeout(timer);
  }, [ticket.attentionStatus, ticket.attentionMessageId, ticketId]);

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const internalTickets = internalSibling
    ? [ticket, internalSibling].filter(item => item?.id)
    : [];
  const internalLabel = internalSibling
    ? internalTickets
        .map(item => item.whatsapp?.name)
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b))
        .join(" ↔ ")
    : null;

  return (
    <div className={classes.root} id="drawer-container">
      <Paper
        variant="outlined"
        elevation={0}
        className={clsx(classes.mainWrapper, {
          [classes.mainWrapperShift]: drawerOpen,
        })}
      >
        <TicketHeader loading={loading}>
          <div className={classes.ticketInfo}>
            <TicketInfo
              contact={contact}
              ticket={ticket}
              internalLabel={internalLabel}
              onClick={handleDrawerOpen}
            />
          </div>
          <div className={classes.ticketActionButtons}>
            <TicketActionButtons ticket={ticket} />
          </div>
        </TicketHeader>
        {internalSibling && (
          <div className={classes.internalToolbar}>
            <FormControl
              variant="outlined"
              size="small"
              className={classes.senderSelect}
            >
              <InputLabel id="internal-sender-label">Enviar como</InputLabel>
              <Select
                labelId="internal-sender-label"
                value={sendTicketId}
                onChange={event => setSendTicketId(Number(event.target.value))}
                label="Enviar como"
              >
                {internalTickets.map(item => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.whatsapp?.name || `Conexión ${item.whatsappId}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        )}
        <ReplyMessageProvider>
          <MessagesList
            key={ticketId}
            ticketId={ticketId}
            isGroup={ticket.isGroup}
            reactionTicketId={sendTicketId}
          ></MessagesList>
          <MessageInput
            ticketStatus={ticket.status}
            sendTicketId={sendTicketId}
          />
        </ReplyMessageProvider>
      </Paper>
      <ContactDrawer
        open={drawerOpen}
        handleDrawerClose={handleDrawerClose}
        contact={contact}
        loading={loading}
      />
    </div>
  );
};

export default Ticket;
